import { useState } from "react";
import { CONSOLE, Contact, PORTFOLIO_LINE, TOOLS, others } from "@authoxi/ui-shared";
import { Router, match, usePageViews, useRoute } from "@authoxi/ui-shared";
import { Home } from "./views/Home";
import {
  Changelog,
  Docs,
  Faq,
  Free,
  GetKey,
  How,
  Partners,
  Pricing,
  Privacy,
  Security,
  Terms,
  Why,
} from "./views/Pages";

/** Every route, in one place. The prerenderer walks this list, so a page that is not here is a
 *  page that ships as an empty div to a crawler — which is the whole reason it is exported. */
export const ROUTES: [path: string, render: () => JSX.Element][] = [
  ["/", () => <Home />],
  ["/why", () => <Why />],
  ["/how", () => <How />],
  ["/security", () => <Security />],
  ["/pricing", () => <Pricing />],
  ["/partners", () => <Partners />],
  ["/start", () => <GetKey />],
  ["/free", () => <Free />],
  ["/changelog", () => <Changelog />],
  ["/faq", () => <Faq />],
  ["/privacy", () => <Privacy />],
  ["/terms", () => <Terms />],
  ["/docs", () => <Docs />],
  ["/contact", () => <ContactPage />],
];

const NAV = [
  ["/why", "Why"],
  ["/how", "How"],
  ["/security", "Security"],
  ["/pricing", "Pricing"],
  ["/partners", "Design partners"],
  ["/faq", "FAQ"],
  ["/contact", "Contact"],
] as const;

export default function App() {
  return (
    <Router>
      <Shell />
    </Router>
  );
}

function Shell() {
  const { path } = useRoute();
  usePageViews(path);
  const view = match(path, ROUTES) ?? <NotFound />;

  return (
    <>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <Nav path={path} />
      <main id="main">{view}</main>
      <Footer />
    </>
  );
}

function Nav({ path }: { path: string }) {
  const [sheet, setSheet] = useState(false);
  return (
    <>
      <header className="nav">
        <div className="nav-in">
          <a href="/" className="brand">
            <i aria-hidden="true" />
            authoxi
          </a>
          <div className="grow" />
          <nav id="navlinks" className="nav-links">
            {NAV.map(([href, label]) => (
              <a href={href} key={href} className={path === href ? "on" : ""}>
                {label}
              </a>
            ))}
            <a href={TOOLS}>Free tools</a>
          </nav>
          <a className="nav-console" href={CONSOLE}>Console</a>
          <a className="btn btn-go nav-desk" href="/start">
            Get a key
          </a>
          <button
            className="theme-btn nav-burger"
            id="burger"
            aria-label="Menu"
            aria-expanded={sheet}
            onClick={() => setSheet((s) => !s)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* A full sheet listing EVERY link. A hamburger that hides half the nav is a nav that
          only exists on desktop. */}
      {sheet && (
        <div className="sheet" data-testid="mobile-sheet" onClick={() => setSheet(false)}>
          <div className="between" style={{ marginBottom: "var(--s5)" }}>
            <span className="brand">
              <i />
              authoxi
            </span>
            <button className="theme-btn" aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          {NAV.map(([href, label]) => (
            <a href={href} key={href}>
              {label}
            </a>
          ))}
          <a href={TOOLS}>Free tools</a>
          <a href="/changelog">Changelog</a>
          <a href="/docs">Docs</a>
          <a className="btn btn-go btn-lg" style={{ marginTop: "var(--s5)" }} href="/start">
            Get a key
          </a>
        </div>
      )}
    </>
  );
}

function Footer() {
  const groups = [
    ["Product", [["/why", "Why"], ["/how", "How"], ["/security", "Security"], ["/pricing", "Pricing"], ["/start", "Get a key"]]],
    // Deep links into the individual tools, not four labels pointing at one page: the free
    // surface is where most first visits land, and a footer that treats it as a single
    // destination hides ten of the eleven things on it.
    ["Free tools", [["/free", "All the free tools"], [`${TOOLS}/grader`, "Trust grader"], [`${TOOLS}/leaderboard`, "Leaderboard"], [`${TOOLS}/directory`, "Directory"], [`${TOOLS}/whois`, "Agent whois"], [`${TOOLS}/verify`, "Offline verifier"], [`${TOOLS}/spec`, "Open specs"]]],
    // The console is on another host (same-origin with the API, so a tenant key never crosses an
    // origin boundary). Absolute, therefore — a relative /console from here would 404 on the
    // marketing distribution.
    ["Your account", [[CONSOLE, "Console"], ["/start", "Get a key"], ["/contact", "Talk to us"]]],
    ["Company", [["/partners", "Design partners"], ["/changelog", "Changelog"], ["/faq", "Questions"], ["/privacy", "Privacy"], ["/terms", "Terms"]]],
  ] as const;

  return (
    <footer className="foot">
      <div className="wide">
        <div className="foot-grid">
          {groups.map(([title, links]) => (
            <div key={title}>
              {/* h5, NOT h6. `.foot h5` is the styled selector — it comes down from
                  landing/src/styles.css, which renders these as h5 — and this file has
                  said h6 since the React port. No rule matched, so every footer group label
                  has shipped in the browser's default h6 style rather than the designed
                  mono-uppercase-faint one, on every page, since launch. Nothing failed: a
                  heading that misses its rule still looks like a heading. */}
              <h5>{title}</h5>
              {links.map(([href, label]) => (
                <a href={href} key={href}>
                  {label}
                </a>
              ))}
            </div>
          ))}
        </div>
        {/* THE REST OF THE PORTFOLIO. Neither site mentioned the other existed, which left every
            visitor evaluating this as a weekend project by somebody who may not be here next
            year. Naming what else runs on the same identity plane costs a footer row.

            Only products with a reachable site — a "trust us" footer must not contain a link to
            a domain that does not resolve. */}
        {others("authoxi").length > 0 ? (
          <div className="foot-portfolio">
            <h5>Also from us</h5>
            <p className="sm muted">{PORTFOLIO_LINE}</p>
            {others("authoxi").map((product) => (
              <a href={product.href} key={product.id} rel="external">
                <strong>{product.name}</strong> — {product.what}
              </a>
            ))}
          </div>
        ) : null}

        <div className="foot-legal">
          <span>© 2026 authoxi. All rights reserved.</span>
          <span>
            Product and company names are the trademarks of their owners. Naming one is
            descriptive and implies no affiliation.
          </span>
        </div>
      </div>
    </footer>
  );
}

function NotFound() {
  return (
    <section className="section">
      <div className="page">
        <h1 className="xl">That page does not exist.</h1>
        <p className="lede">
          <a href="/">Back to the start</a>, or <a href={TOOLS}>the free tools</a>.
        </p>
      </div>
    </section>
  );
}

/**
 * The contact page.
 *
 * Distinct from `Ask`, which trades an email for access. Somebody arriving here has a question
 * rather than an intention to sign up, and the note field is what tells those two apart in the
 * leads table — an address with no sentence attached is a lead you have to open a conversation
 * to qualify.
 */
function ContactPage() {
  return (
    <section className="section">
      <div className="page">
        <Contact source="landing" surface="contact" />
      </div>
    </section>
  );
}
